import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * Mobile nav drawer for the custom YUSUFZ header menu.
 *
 * The header itself has `contain: layout style` (see sections/header.liquid),
 * which makes it a containing block for any `position: fixed` descendant --
 * the same effect a `transform` has. Left alone, that means the drawer isn't
 * actually fixed to the viewport at all: it's fixed relative to the header
 * bar's own small box, so scrolling down before opening it made the drawer
 * (and its close button) render off-screen above the header, with no way to
 * reach it and scroll locked.
 *
 * The fix: move the drawer panel to be a direct child of <body> once, so it
 * escapes that containing block entirely. The trigger button stays put in
 * the header; only the portal (backdrop + sliding panel) moves.
 *
 * @typedef {{ trigger: HTMLButtonElement, portal: HTMLElement, menuDrawer: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class YusufzHeaderDrawer extends Component {
  requiredRefs = ['trigger', 'portal', 'menuDrawer'];

  /** Elements made inert while the drawer is open, restored on close. @type {Element[]} */
  #background = [];

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.portal.parentElement !== document.body) {
      document.body.appendChild(this.refs.portal);
    }

    // Listen on the document, not on this element. The panel is portalled out
    // to <body>, so once focus is inside it, key events bubble portal -> body
    // -> document and never pass through this custom element at all.
    document.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  get isOpen() {
    return this.refs.portal.classList.contains('menu-open');
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open() {
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('menu-open');
    this.refs.portal.classList.add('menu-open');
    lockScroll(this);

    this.#setBackgroundInert();

    this.#focusIntoDrawer();
  }

  /**
   * Hides everything except the drawer from assistive tech and the tab order.
   *
   * The panel is not always a direct child of <body> -- depending on hydration
   * order it can still be nested inside this component, inside .page-wrapper.
   * So "inert every body child except the portal" would inert an ancestor of
   * the drawer and make the drawer itself unreachable. Instead, walk from the
   * drawer up to <body> and inert each ancestor's *siblings*, which isolates
   * the drawer wherever it happens to live.
   */
  #setBackgroundInert() {
    this.#background = [];

    let node = this.refs.menuDrawer;
    while (node && node.parentElement && node !== document.body) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || sibling.hasAttribute('inert')) continue;
        sibling.setAttribute('inert', '');
        this.#background.push(sibling);
      }
      node = node.parentElement;
    }
  }

  /**
   * Moves focus into the open drawer.
   *
   * The panel animates out of `visibility: hidden`, and an element that isn't
   * visible yet can't take focus -- so calling focus() on the next frame
   * silently does nothing and focus is left on <body>. Retry across a few
   * frames until it actually lands, then trap.
   *
   * @param {number} [attempt]
   */
  #focusIntoDrawer(attempt = 0) {
    requestAnimationFrame(() => {
      if (!this.isOpen) return;

      const first = this.refs.menuDrawer.querySelector('button, a[href]');
      if (first instanceof HTMLElement) {
        first.focus();
        if (document.activeElement === first) {
          trapFocus(this.refs.menuDrawer);
          return;
        }
      }

      if (attempt < 20) this.#focusIntoDrawer(attempt + 1);
    });
  }

  close() {
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('menu-open');
    this.refs.portal.classList.remove('menu-open');
    unlockScroll(this);

    for (const element of this.#background) element.removeAttribute('inert');
    this.#background = [];

    removeTrapFocus();

    // Hand focus back to the button that opened the drawer. removeTrapFocus()
    // only detaches listeners; without this, focus falls to <body> and a
    // keyboard user restarts from the top of the document (WCAG 2.4.3).
    // inert is already cleared above, so the trigger is focusable again.
    this.refs.trigger.focus();
  }
}

if (!customElements.get('yusufz-header-drawer')) {
  customElements.define('yusufz-header-drawer', YusufzHeaderDrawer);
}
